import React, { useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box, Button, Card, Tooltip, Typography } from "@mui/material";
import {
  AddCircle as CreateBillIcon,
  Payment as AddPaymentIcon,
  ExitToApp as MoveOutIcon,
  PowerOff as DisconnectIcon,
  Person as PersonIcon,
  Folder as FileIcon,
  Work as WorkIcon,
  Email as InviteIcon,
} from "@mui/icons-material";
import { setSelectedUserProfile } from "modules/userProfiles/api/actions";
import { setSnackbar } from "modules/snackbar/api/snackbar";
import axios from "axios";
import { DIALOGS } from "../hooks/useDialogManager";
import { needsMessage, useCan } from "modules/settings/accessRoles";

function resolveAccountUserProfile(details) {
  if (!details) return null;
  if (Array.isArray(details)) return details[0] || null;
  // Some flows store a single user object on details
  if (details.id || details.uuid) return details;
  return null;
}

const QuickActions = React.memo(function QuickActions({ openDialog }) {
  const can = useCan();
  const dispatch = useDispatch();
  const userProfiles = useSelector((state) => state.userProfiles);
  const account = useSelector((state) => state.account);
  const hasUserProfile = Boolean(resolveAccountUserProfile(userProfiles.details));

  const handleUserProfileClick = useCallback(() => {
    const profile = resolveAccountUserProfile(userProfiles.details);
    if (!profile) {
      dispatch(
        setSnackbar(
          true,
          "warning",
          "No user profile is linked to this account yet."
        )
      );
      return;
    }
    dispatch(setSelectedUserProfile(profile));
    openDialog(DIALOGS.USER_PROFILE);
  }, [dispatch, userProfiles.details, openDialog]);

  // Resend the account invitation directly from the account page. This does NOT
  // require a linked user profile (the invitation is what creates one), so it
  // covers accounts that have no profile yet — where the User Profile card, and
  // therefore its "Resend Account Invitation" menu item, is unreachable.
  const handleResendInvitation = useCallback(() => {
    const acct = account?.data?.account;
    if (!acct || !acct.uuid) {
      dispatch(setSnackbar(true, "error", "Account is still loading — try again in a moment."));
      return;
    }
    if (!acct.email) {
      dispatch(setSnackbar(true, "error", "This account has no email on file to send an invitation to."));
      return;
    }
    dispatch(setSnackbar(true, "warning", "Sending account invitation email..."));
    axios
      .post("accountcreation/invitation", {
        accountUuid: acct.uuid,
        accountNum: acct.accountNum,
        name: `${acct.firstName || ""} ${acct.lastName || ""}`.trim(),
        email: acct.email,
      })
      .then((response) => {
        if (response.status === 200) {
          dispatch(setSnackbar(true, "success", "Invitation sent."));
        } else {
          throw new Error("Invitation failed");
        }
      })
      .catch(() => {
        dispatch(setSnackbar(true, "error", "Invitation unable to send."));
      });
  }, [dispatch, account]);

  const buttonSx = {
    flex: "1 1 200px",
    minWidth: "200px",
    py: 1.5,
    fontSize: "0.875rem",
    textTransform: "none",
    fontWeight: 500,
  };

  // A role-gated action stays visible but disabled, with the reason on hover.
  const gated = (capability, button) =>
    can(capability) ? button : (
      <Tooltip key={capability} title={needsMessage(capability)} arrow>
        <Box component="span" sx={{ display: "flex", flex: "1 1 200px", minWidth: "200px" }}>
          {React.cloneElement(button, { disabled: true, sx: { ...buttonSx, flex: 1 } })}
        </Box>
      </Tooltip>
    );

  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Typography
        variant="h6"
        fontWeight="600"
        sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
      >
        <WorkIcon color="primary" fontSize="small" />
        Quick Actions
      </Typography>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {gated("bills", (
          <Button
          variant="contained"
          startIcon={<CreateBillIcon />}
          onClick={() => openDialog(DIALOGS.BILL_FORM)}
          sx={buttonSx}
        >
          Create Bill
        </Button>
        ))}
        {gated("payments", (
          <Button
          variant="contained"
          startIcon={<AddPaymentIcon />}
          onClick={() => openDialog(DIALOGS.PAYMENT_FORM)}
          sx={buttonSx}
        >
          Add Payment
        </Button>
        ))}
        {gated("moves", (
          <Button
          variant="outlined"
          startIcon={<MoveOutIcon />}
          onClick={() => openDialog(DIALOGS.MOVE_OUT)}
          sx={buttonSx}
        >
          Move Out
        </Button>
        ))}
        {gated("disconnects", (
          <Button
          variant="outlined"
          startIcon={<DisconnectIcon />}
          onClick={() => openDialog(DIALOGS.DISCONNECTS)}
          sx={buttonSx}
        >
          Disconnect
        </Button>
        ))}
        <Button
          variant="outlined"
          startIcon={<PersonIcon />}
          onClick={handleUserProfileClick}
          sx={buttonSx}
        >
          User Profile
        </Button>
        <Tooltip
          title={
            hasUserProfile
              ? "A user profile already exists for this account."
              : ""
          }
          arrow
        >
          <span>
            <Button
              variant="outlined"
              startIcon={<InviteIcon />}
              onClick={handleResendInvitation}
              disabled={hasUserProfile}
              sx={buttonSx}
            >
              Resend Invitation
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="outlined"
          startIcon={<FileIcon />}
          onClick={() => openDialog(DIALOGS.ACCOUNT_FILES)}
          sx={buttonSx}
        >
          Files
        </Button>
      </Box>
    </Card>
  );
});

export default QuickActions;
